import React, { useMemo, useCallback } from 'react';
import { SelectField as Select } from '@oneaudi/unified-web-components';
import { toInteger } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Filter, FilterFrame, FilterSelection } from '../Filters.style';
import { RangeFilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import { PowerFilterWrapper } from './PowerSelector.style';
import useAnimations from '../../../hooks/Animations/useAnimations';

interface PowerSelectorTypes {
  filterType: string;
  temporaryFilters: FilterTypes;
  filterOptions: {
    min: { [key: string]: RangeFilterOption };
    max: { [key: string]: RangeFilterOption };
  };
  openedFiltersList: ExposedFiltersTypes;
  handleChangeRange: (filterType: string, filterValue: number[]) => void;
  handleExposeFilter: (arg: string) => void;
}

const PowerSelector: React.FC<PowerSelectorTypes> = ({
  filterType,
  temporaryFilters,
  handleChangeRange,
  handleExposeFilter,
  filterOptions,
  openedFiltersList,
}) => {
  const { t } = useTranslation();

  const selectedRange = useMemo(() => {
    return temporaryFilters.power;
  }, [temporaryFilters.power]);

  const optionDomain = useMemo(() => {
    // Convert the strings to numbers and find the minimum and maximum value
    const minKeys = Object.keys(filterOptions.min);
    const maxKeys = Object.keys(filterOptions.max);
    const min = Math.min(...minKeys.map((item) => parseInt(item, 10)));
    const max = Math.max(...maxKeys.map((item) => parseInt(item, 10)));

    return {
      minOption: min,
      maxOption: max,
    };
  }, [filterOptions]);

  // disable showing label inside select component when there is a value selected bu user
  const RangeHasValue = useMemo(() => {
    return !!selectedRange.length;
  }, [selectedRange]);

  const selectedRangeLabel = useMemo(() => {
    const [minValue, maxValue] = selectedRange;

    if (minValue < 0 && maxValue < 0) return '';

    const formatLabel = (label: 'min' | 'max', value: number) =>
      value >= 0 ? `${label}: ${filterOptions[label][value]?.label}` : '';

    const minLabel = formatLabel('min', minValue);
    const maxLabel = formatLabel('max', maxValue);

    return `${minLabel}${minLabel && maxLabel ? ', ' : ''}${maxLabel}`;
  }, [filterOptions, selectedRange]);

  // Handling changes to the power range (min or max) based on user selection
  const handleChangeValue = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>, isMin: boolean) => {
      // Parsing the input value to an integer, providing a default value of -1 if it's not a valid integer
      const newInputValue = Number.isNaN(event.target.value) ? -1 : toInteger(event.target.value);
      const [minValue, maxValue] = selectedRange;

      const newValue = isMin
        ? [newInputValue, maxValue || optionDomain.maxOption]
        : [minValue || optionDomain.minOption, newInputValue];
      handleChangeRange(filterType, newValue);
    },
    [filterType, handleChangeRange, optionDomain.maxOption, optionDomain.minOption, selectedRange],
  );

  const renderOptions = useCallback(
    (options: { [key: string]: RangeFilterOption }, isMin: boolean) => {
      return Object.keys(options).map((filterKey) => {
        const { ariaLabel, label, value } = options[filterKey];
        return (
          <option
            key={ariaLabel}
            aria-label={ariaLabel}
            value={value.toString()}
            disabled={
              (isMin && selectedRange.length > 1 && value >= selectedRange[1]) ||
              (!isMin && selectedRange.length > 0 && value <= selectedRange[0])
            }
          >
            {label}
          </option>
        );
      });
    },
    [selectedRange],
  );

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedRangeLabel}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="power-filter">
              <FilterFrame style={filtersAccordianAnimation}>
                <PowerFilterWrapper>
                  <Select
                    id={`select_min_${filterType}`}
                    label={t(`vlp.filters.filterOptions.${filterType}.minLabel`)}
                    onChange={(e) => handleChangeValue(e, true)}
                    value={selectedRange[0]?.toString() || ''}
                    // hideLabelOptional
                    className={RangeHasValue ? 'hasValue' : ''}
                    required
                    data-testid="select_min_power"
                  >
                    {renderOptions(filterOptions.min, true)}
                  </Select>
                  <Select
                    id={`select_max_${filterType}`}
                    label={t(`vlp.filters.filterOptions.${filterType}.maxLabel`)}
                    onChange={(e) => handleChangeValue(e, false)}
                    value={selectedRange[1]?.toString() || ''}
                    // hideLabelOptional
                    className={RangeHasValue ? 'hasValue' : ''}
                    required
                    data-testid="select_max_power"
                  >
                    {renderOptions(filterOptions.max, false)}
                  </Select>
                </PowerFilterWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default PowerSelector;
